import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Anchor, LogOut, User, MessageSquare, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV_LINKS } from "@/lib/data";
import { useAuth } from "@/context/AuthContext";
import MessagesDrawer from "@/components/MessagesDrawer";
import SecretAccess from "@/components/SecretAccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [secretOpen, setSecretOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const { user, openAuth, logout, setAuthOpen } = useAuth();
  const clickTimes = useRef([]);
  const openTimer = useRef(null);

  // Register a rapid click toward the easter egg. 10 clicks within 3s opens the terminal.
  const registerRapidClick = useCallback(() => {
    if (secretOpen) return;
    const now = Date.now();
    clickTimes.current = [...clickTimes.current.filter((t) => now - t < 3000), now];
    if (clickTimes.current.length >= 10) {
      clickTimes.current = [];
      if (openTimer.current) clearTimeout(openTimer.current);
      setAuthOpen(false);
      setSecretOpen(true);
    }
  }, [secretOpen, setAuthOpen]);

  // Easter egg trigger #1: clicking ANYWHERE on the home screen 10x fast.
  useEffect(() => {
    window.addEventListener("click", registerRapidClick);
    return () => window.removeEventListener("click", registerRapidClick);
  }, [registerRapidClick]);

  // Sign In button: a normal single click opens login (debounced so a rapid
  // click burst doesn't flash the modal while the egg sequence completes).
  const handleSignIn = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => openAuth("login"), 320);
  };

  const loadUnread = useCallback(async () => {
    if (!user) { setUnread(0); return; }
    try { const { data } = await axios.get(`${API}/me/messages/unread`); setUnread(data.unread); }
    catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    loadUnread();
    if (!user) return;
    const id = setInterval(loadUnread, 20000);
    return () => clearInterval(id);
  }, [user, loadUnread]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#0B0F14]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" data-testid="nav-brand-logo" className="flex items-center gap-2.5 group">
          <span className="h-9 w-9 grid place-items-center bg-[#FF5E1E] text-[#0B0F14]">
            <Anchor size={20} strokeWidth={2.5} />
          </span>
          <span className="font-display font-black uppercase tracking-tight text-lg leading-none">
            Reentry<span className="text-[#FF5E1E]">.</span>Staffing
          </span>
        </a>

        <div className="hidden lg:flex flex-1 justify-center items-center gap-6 xl:gap-7 px-6">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#8FA3B8] hover:text-[#F4F6F8] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <a href="#dashboard" data-testid="nav-user-chip" className="flex items-center gap-2 border border-white/15 px-3 py-1.5 hover:border-[#FF5E1E]/60 transition-colors">
                <span className="h-5 w-5 grid place-items-center bg-[#FF5E1E] text-[#0B0F14]"><User size={12} /></span>
                <span className="font-mono-tech text-[10px] tracking-[0.12em] text-white truncate max-w-[120px]">{user.name}</span>
              </a>
              <button data-testid="nav-messages-button" onClick={() => setMsgOpen(true)} className="relative text-[#8FA3B8] hover:text-white p-2" title="Messages">
                <MessageSquare size={16} />
                {unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 grid place-items-center bg-[#FF5E1E] text-[#0B0F14] text-[9px] font-bold">{unread}</span>}
              </button>
              <button data-testid="nav-logout-button" onClick={logout} className="text-[#8FA3B8] hover:text-white p-2" title="Log out"><LogOut size={16} /></button>
            </>
          ) : (
            <>
              <button data-testid="nav-admin-button" onClick={() => openAuth("login", "admin@reentrystaffing.com")} title="Admin login" className="flex items-center gap-1.5 font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#8FA3B8] hover:text-[#FF5E1E] transition-colors">
                <ShieldCheck size={14} /> Admin
              </button>
              <span className="h-4 w-px bg-white/15" />
              <button data-testid="nav-signin-button" onClick={handleSignIn} className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#8FA3B8] hover:text-white transition-colors">
                Sign In
              </button>
            </>
          )}
          <Button data-testid="nav-get-started-button" onClick={() => (user ? (window.location.hash = "#apply") : openAuth("register"))} className="rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase tracking-wide">
            Get Shipyard Ready
          </Button>
        </div>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-white p-2">
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#0B0F14] border-b border-white/10"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="font-mono-tech text-xs uppercase tracking-[0.18em] text-[#8FA3B8]">
                  {l.label}
                </a>
              ))}
              <Button asChild className="rounded-none bg-[#FF5E1E] text-[#0B0F14] font-display font-bold uppercase mt-2">
                <a href="#apply" onClick={() => setOpen(false)}>Get Shipyard Ready</a>
              </Button>
              {user && (
                <button data-testid="nav-mobile-messages" onClick={() => { setOpen(false); setMsgOpen(true); }} className="flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.18em] text-[#8FA3B8]">
                  <MessageSquare size={14} /> Messages {unread > 0 && <span className="min-w-[16px] h-4 px-1 grid place-items-center bg-[#FF5E1E] text-[#0B0F14] text-[9px] font-bold">{unread}</span>}
                </button>
              )}
              {!user && (
                <button data-testid="nav-mobile-admin" onClick={() => { setOpen(false); openAuth("login", "admin@reentrystaffing.com"); }} className="flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.18em] text-[#8FA3B8]">
                  <ShieldCheck size={14} /> Admin Login
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {user && <MessagesDrawer open={msgOpen} onOpenChange={setMsgOpen} onRead={loadUnread} />}
      <SecretAccess open={secretOpen} onClose={() => setSecretOpen(false)} />
    </motion.header>
  );
}
